/**
 * Product page add to cart via REST — toast + cart drawer, no WooCommerce banner.
 */

import { apiFetch, openCartDrawer, announceCart } from './cart-drawer.js';
import { showToast } from './toast.js';

function getConfig() {
	return window.doneCommerce || {};
}

function readProductId(form) {
	const productId = form.querySelector('[name="product_id"]')?.value;
	const addToCart = form.querySelector('[name="add-to-cart"]')?.value;
	return Number(productId || addToCart || 0);
}

function readQuantity(form) {
	const qty = Number.parseInt(form.querySelector('input[name="quantity"]')?.value || '1', 10);
	return Number.isFinite(qty) && qty > 0 ? qty : 1;
}

function readVariation(form) {
	const variationId = Number(form.querySelector('input[name="variation_id"]')?.value || 0);
	const variation = {};

	form.querySelectorAll('[name^="attribute_"]').forEach((field) => {
		const name = field.getAttribute('name');
		if (!name) {
			return;
		}
		variation[name] = field.value || '';
	});

	return { variationId, variation };
}

function groupedItems(form) {
	return [...form.querySelectorAll('input[name^="quantity["]')]
		.map((input) => {
			const match = String(input.name).match(/^quantity\[(\d+)\]$/);
			const quantity = Number.parseInt(input.value || '0', 10);
			if (!match || !Number.isFinite(quantity) || quantity < 1) {
				return null;
			}
			return { product_id: Number(match[1]), quantity };
		})
		.filter(Boolean);
}

function setBusy(button, busy, originalText) {
	if (!button) {
		return;
	}

	button.disabled = busy;
	button.classList.toggle('is-loading', busy);
	button.textContent = busy
		? getConfig().i18n?.addingToCart || 'Adding…'
		: originalText;
}

async function addItems(items) {
	for (const item of items) {
		await apiFetch('done-commerce/v1/cart/items', {
			method: 'POST',
			body: JSON.stringify(item),
		});
	}
}

export function initPdpAddToCart() {
	if (!document.body.classList.contains('single-product')) {
		return;
	}

	const form = document.querySelector('.dc-pdp__summary form.cart, .summary form.cart');
	if (!form || form.classList.contains('product_type_external')) {
		return;
	}

	const button = form.querySelector('.single_add_to_cart_button');
	if (!button) {
		return;
	}

	form.addEventListener('submit', async (event) => {
		event.preventDefault();

		if (button.disabled && !button.classList.contains('is-loading')) {
			const needOptions = getConfig().i18n?.selectProductOptions || 'Please select product options.';
			showToast(needOptions, { variant: 'error' });
			return;
		}

		const i18n = getConfig().i18n || {};
		let items = [];

		if (form.classList.contains('grouped_form') || form.querySelector('input[name^="quantity["]')) {
			items = groupedItems(form);
			if (!items.length) {
				showToast(i18n.chooseQuantity || 'Please choose a quantity.', { variant: 'error' });
				return;
			}
		} else {
			const productId = readProductId(form);
			if (!productId) {
				return;
			}

			const { variationId, variation } = readVariation(form);
			if (form.classList.contains('variations_form') && !variationId) {
				showToast(i18n.selectProductOptions || 'Please select product options.', { variant: 'error' });
				return;
			}

			items = [
				{
					product_id: productId,
					quantity: readQuantity(form),
					variation_id: variationId,
					variation,
				},
			];
		}

		const originalText = button.textContent;
		setBusy(button, true, originalText);

		try {
			await addItems(items);
			const message = i18n.addedToCart || 'Added to cart';
			showToast(message);
			openCartDrawer({ announce: message });
		} catch (error) {
			const fail = error?.message || i18n.addToCartFailed || 'Unable to add to cart';
			showToast(fail, { variant: 'error' });
			announceCart(fail);
		} finally {
			setBusy(button, false, originalText);
		}
	});
}
